import { Color, Filter, GlProgram, GpuProgram, UniformGroup } from 'pixi.js';

import fragment from './cartoonText.frag?raw';
import vertex from './cartoonText.vert?raw';
import source from './cartoonText.wgsl?raw';
import { isTouchDevice } from '@/app';
import { currentPalette } from '../../palettes';

const defaultOptions = {
  x: 0,
  y: 0,
  thickness:  2,
  borderColor:  0xfdfdfd, //HIGHLIGHT colour //0xfdfdfd | 0xffff05
  color: 0xffffff , //0xffffff | 0x0fff0f
  mouse: [300, 0],
  screenSize: [1920,1080],
  origin: [0,0],
  filterSize: [0,0],
  depth: 0.0,
  isTouchDevice: 1,
  depthShading: 1,
  dropShadows: 1,
  hardLighting: 1,
  softLighting: 1,
  castDistance: 50,
  scale: 1
};

// /**
//  * CartoonTextFilter creates a cartoon-style text effect with outlines, shadows, and gradients.
//  *
//  * @example
//  * ```js
//  * import { CartoonTextFilter } from './CartoonTextFilter';
//  *
//  * const cartoonFilter = new CartoonTextFilter({
//  *   thickness: 2.0,
//  *   borderColor: 0x000000,
//  *   topColor: 0xFFFFFF,
//  *   bottomColor: 0x888888
//  * });
//  * text.filters = [cartoonFilter];
//  * ```
export class CartoonTextFilter extends Filter {
  constructor(options: any) {
    const { x, y, thickness, borderColor, color, mouse, screenSize, origin, filterSize, mousePressed, depth, isTouchDevice, 
      depthShading, dropShadows, hardLighting, softLighting, castDistance, scale} = {
      ...defaultOptions,
      ...options,
    };

    const gpuProgram = GpuProgram.from({
      vertex: {
        source,
        entryPoint: 'mainVertex',
      },
      fragment: {
        source,
        entryPoint: 'mainFragment',
      },
    });

    const glProgram = GlProgram.from({
      vertex,
      fragment,
      name: 'cartoon-text-filter',
    });

    super({
      gpuProgram,
      glProgram,
      padding: thickness * 5.0,
      resources: {
        cartoonTextUniforms: new UniformGroup({
          uThickness: { value: thickness, type: 'f32' },
          uBorderColor: { value: new Color(borderColor), type: 'vec3<f32>' },
          uColor: { value: new Color(color), type: 'vec3<f32>' },
          uDirection: {value: [x, y], type: 'vec2<f32>' },
          uScreenSize: {value: [screenSize], type: 'vec2<f32>'},
          uMouse: {value: [mouse[0], mouse[1]], type: 'vec2<f32>'},
          uOrigin: { value: [origin[0], origin[1]], type: 'vec2<f32>'},
          uFilterSize: { value: [filterSize[0], filterSize[1]], type: 'vec2<f32>'},
          uMousePressed: {value: mousePressed , type: 'f32'},
          uDepth: {value: depth, type: 'f32'},
          uIsTouchDevice: {value: isTouchDevice? 1: 0, type: 'f32'},
          uDepthShading: {value: depthShading, type: 'f32'},
          uDropShadows: {value: dropShadows, type: 'f32'},
          uHardLighting: {value: hardLighting, type: 'f32'},
          uCastDistance: {value: castDistance, type: 'f32'},
          uSoftLighting: {value: softLighting, type: 'f32'},
          uScale: {value: scale, type: 'f32'}
        }),
      },
    });

    
  }

  get x(): number {
    return this.resources.cartoonTextUniforms.uniforms.uX;
  }

  set x(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uX = value;
  }

  get y(): number {
    return this.resources.cartoonTextUniforms.uniforms.uY;
  }

  set y(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uY = value;
  }

  get direction(): number[] {
    const direction = this.resources.cartoonTextUniforms.uniforms.uDirection;
    return [direction[0],direction[1]];
  }

  set direction(value: number[]) {
    this.resources.cartoonTextUniforms.uniforms.uDirection[0] = value[0];
    this.resources.cartoonTextUniforms.uniforms.uDirection[1]  = value[1];
  }

  get screenSize(): number[] {
    const screenSize = this.resources.cartoonTextUniforms.uniforms.uScreen;
    console.log("Screen size...")
    console.log(screenSize)
    return [screenSize[0], screenSize[1]];
  }

  set screenSize(value : number[]) {
    this.resources.cartoonTextUniforms.uniforms.uScreenSize[0] = value[0];
    this.resources.cartoonTextUniforms.uniforms.uScreenSize[1]  = value[1];
  }

  get mouse(): number[] {
    const mouse = this.resources.cartoonTextUniforms.uniforms.uMouse;
    return mouse;
  }

  set mouse(value : number[]) {
    this.resources.cartoonTextUniforms.uniforms.uMouse = value;
  }

  set origin( value: number[]) {
    this.resources.cartoonTextUniforms.uniforms.uOrigin = value;
  }

  

  /**
   * The thickness of the outline effect.
   * @default 2.0
   */
  get thickness() {
    const uniforms = this.resources.cartoonTextUniforms.uniforms;

    return uniforms.uThickness;
  }

  set thickness(value) {
    const uniforms = this.resources.cartoonTextUniforms.uniforms;

    uniforms.uThickness = value;
  }

  set filterSize( value: number[]) {
    this.resources.cartoonTextUniforms.uniforms.uFilterSize = value;
  }

  set mousePressed(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uMousePressed = value;
  }

  set depth ( value: number) {
    this.resources.cartoonTextUniforms.uniforms.uDepth = value;
  }

  set isTouchDevice (value: number) {
    this.resources.cartoonTextUniforms.uniforms.uIsTouchDevice = value;
  }

  set color(value: number[]) {
    this.resources.cartoonTextUniforms.uniforms.uBorderColor = new Color(value[0]);
    this.resources.cartoonTextUniforms.uniforms.uColor = new Color(value[1]);
  }

  set depthShading(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uDepthShading = value;
  }
  
  set dropShadows(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uDropShadows = value;
  }

  set hardLighting(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uHardLighting = value;
  }

  set softLighting(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uSoftLighting = value;
  }

  set castDistance(value: number) {
    this.resources.cartoonTextUniforms.uniforms.uCastDistance = value;
  }

  get scale(){
    return this.resources.cartoonTextUniforms.uniforms.uScale;
  }

  set scale(value:number){
    this.resources.cartoonTextUniforms.uniforms.uScale = value;
  }

}
